import { memo, useCallback } from 'react';
import { GRID_SIZE } from '../types';

export interface CanvasResizeHandlesProps {
  x: number;
  y: number;
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  onResize: (x: number, y: number, width: number, height: number) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  /** When true, only show corner handles and enforce aspect ratio. */
  lockAspectRatio?: boolean;
}

type HandlePosition = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

const HANDLE_SIZE = 8;

function getHandleStyle(pos: HandlePosition, width: number, height: number): React.CSSProperties {
  const HS = HANDLE_SIZE;
  const base: React.CSSProperties = {
    position: 'absolute',
    width: HS,
    height: HS,
    backgroundColor: 'white',
    border: '1.5px solid #3b82f6',
    borderRadius: 1,
    zIndex: 10,
    pointerEvents: 'auto',
  };

  switch (pos) {
    case 'n':  return { ...base, left: width / 2 - HS / 2, top: -HS / 2, cursor: 'n-resize' };
    case 'ne': return { ...base, right: -HS / 2, top: -HS / 2, cursor: 'ne-resize' };
    case 'e':  return { ...base, right: -HS / 2, top: height / 2 - HS / 2, cursor: 'e-resize' };
    case 'se': return { ...base, right: -HS / 2, bottom: -HS / 2, cursor: 'se-resize' };
    case 's':  return { ...base, left: width / 2 - HS / 2, bottom: -HS / 2, cursor: 's-resize' };
    case 'sw': return { ...base, left: -HS / 2, bottom: -HS / 2, cursor: 'sw-resize' };
    case 'w':  return { ...base, left: -HS / 2, top: height / 2 - HS / 2, cursor: 'w-resize' };
    case 'nw': return { ...base, left: -HS / 2, top: -HS / 2, cursor: 'nw-resize' };
  }
}

const ALL_HANDLES: HandlePosition[] = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
const CORNER_HANDLES: HandlePosition[] = ['ne', 'se', 'sw', 'nw'];

function CanvasResizeHandlesInner({
  x, y, width, height, minWidth, minHeight, onResize, onResizeStart, onResizeEnd, lockAspectRatio,
}: CanvasResizeHandlesProps) {
  const handlePointerDown = useCallback((pos: HandlePosition, e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onResizeStart?.();

    const startScreenX = e.clientX;
    const startScreenY = e.clientY;
    const startX = x;
    const startY = y;
    const startW = width;
    const startH = height;
    const aspect = startW / startH;

    const canvasEl = (e.target as HTMLElement).closest('[data-canvas-canvas]') as HTMLElement | null;
    const zoom = canvasEl ? parseFloat(canvasEl.dataset.zoom ?? '1') : 1;

    // Find the parent entity element for imperative DOM updates during resize
    const entityEl = (e.target as HTMLElement).closest('[data-canvas-entity]') as HTMLElement | null;

    let lastX = startX;
    let lastY = startY;
    let lastW = startW;
    let lastH = startH;

    function computeResize(rawDx: number, rawDy: number) {
      let newX = startX;
      let newY = startY;
      let newW = startW;
      let newH = startH;

      if (lockAspectRatio) {
        const growsRight = pos === 'se' || pos === 'ne';
        const rawW = growsRight ? startW + rawDx : startW - rawDx;
        newW = Math.max(minWidth, Math.round(rawW));
        newH = Math.max(minHeight, Math.round(newW / aspect));

        if (pos === 'sw' || pos === 'nw') {
          newX = Math.round((startX + startW - newW) / GRID_SIZE) * GRID_SIZE;
        }
        if (pos === 'ne' || pos === 'nw') {
          newY = Math.round((startY + startH - newH) / GRID_SIZE) * GRID_SIZE;
        }
      } else {
        if (pos === 'e' || pos === 'ne' || pos === 'se') {
          newW = Math.max(minWidth, Math.round((startW + rawDx) / GRID_SIZE) * GRID_SIZE);
        }
        if (pos === 'w' || pos === 'nw' || pos === 'sw') {
          const dx = Math.round(rawDx / GRID_SIZE) * GRID_SIZE;
          const maxDx = startW - minWidth;
          const clampedDx = Math.min(dx, maxDx);
          newX = startX + clampedDx;
          newW = startW - clampedDx;
        }
        if (pos === 's' || pos === 'se' || pos === 'sw') {
          newH = Math.max(minHeight, Math.round((startH + rawDy) / GRID_SIZE) * GRID_SIZE);
        }
        if (pos === 'n' || pos === 'ne' || pos === 'nw') {
          const dy = Math.round(rawDy / GRID_SIZE) * GRID_SIZE;
          const maxDy = startH - minHeight;
          const clampedDy = Math.min(dy, maxDy);
          newY = startY + clampedDy;
          newH = startH - clampedDy;
        }
      }

      return { newX, newY, newW, newH };
    }

    function handleMove(ev: PointerEvent) {
      const rawDx = (ev.clientX - startScreenX) / zoom;
      const rawDy = (ev.clientY - startScreenY) / zoom;
      const { newX, newY, newW, newH } = computeResize(rawDx, rawDy);

      lastX = newX;
      lastY = newY;
      lastW = newW;
      lastH = newH;

      // Imperative DOM update — no store dispatch during drag
      if (entityEl) {
        entityEl.style.left = `${newX}px`;
        entityEl.style.top = `${newY}px`;
        entityEl.style.width = `${newW}px`;
        entityEl.style.height = `${newH}px`;
      }
    }

    function handleUp() {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      // Commit final values to store in a single dispatch
      if (lastX !== startX || lastY !== startY || lastW !== startW || lastH !== startH) {
        onResize(lastX, lastY, lastW, lastH);
      }
      onResizeEnd?.();
    }

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  }, [x, y, width, height, minWidth, minHeight, onResize, onResizeStart, onResizeEnd, lockAspectRatio]);

  const handles = lockAspectRatio ? CORNER_HANDLES : ALL_HANDLES;

  return (
    <>
      {handles.map(pos => (
        <div
          key={pos}
          data-resize-handle={pos}
          data-capture-hide
          style={getHandleStyle(pos, width, height)}
          onPointerDown={(e) => handlePointerDown(pos, e)}
        />
      ))}
    </>
  );
}

export const CanvasResizeHandles = memo(CanvasResizeHandlesInner);
